import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import useOwnerSession from '../hooks/useOwnerSession';
import HotelForm from './HotelForm';
import SpaForm from './SpaForm';
import TourForm from './TourForm';
import TravelPackageForm from './TravelPackageForm';
import PartnerShell from '../components/PartnerShell';
import PartnerAuth from '../components/PartnerAuth';
import './OwnerRegistration.css';
import './OwnerPortal.css';

const LISTING_GROUPS = [
  { key: 'hotels', label: 'Stays', single: 'stay', icon: '🏨', form: HotelForm },
  { key: 'spas', label: 'Spas', single: 'spa', icon: '💆', form: SpaForm },
  { key: 'tours', label: 'Tours & Activities', single: 'tour', icon: '🎫', form: TourForm },
  { key: 'travel-packages', label: 'Travel Packages', single: 'travel package', icon: '✈️', form: TravelPackageForm },
];

const STATUS_LABELS = {
  active: { text: 'Live', className: 'owner-status-live' },
  pending: { text: 'Under review', className: 'owner-status-pending' },
  inactive: { text: 'Paused', className: 'owner-status-inactive' },
};

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const imageUrl = (path) => {
  if (!path) return '';
  return /^https?:\/\//.test(path) ? path : `${API_BASE_URL_WITHOUT_API}${path}`;
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const OwnerPortal = () => {
  const navigate = useNavigate();
  const { type: editType, id: editId } = useParams();
  const [session, setSession] = useOwnerSession();

  // Login
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Listings
  const [listings, setListings] = useState(null);
  const [listingsError, setListingsError] = useState('');
  // Listing the owner asked to edit; a live one gets a review notice first
  const [pendingEdit, setPendingEdit] = useState(null);

  const resetMessages = () => {
    setError('');
    setMessage('');
  };

  const logOut = useCallback((note) => {
    setSession(null);
    setListings(null);
    setStep('phone');
    setOtp('');
    if (note) setMessage(note);
  }, [setSession]);

  const loadListings = useCallback(async (token) => {
    try {
      setListingsError('');
      const response = await axios.get(`${API_BASE_URL}/public/owners/me/listings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setListings(response.data?.data || {});
    } catch (err) {
      if (err?.response?.status === 401) {
        logOut('Your session has expired. Please log in again.');
        return;
      }
      setListingsError(err?.response?.data?.message || 'Could not load your listings. Please try again.');
      setListings({});
    }
  }, [logOut]);

  // Reload on login and whenever the owner comes back from an edit
  useEffect(() => {
    if (session?.token && !editType) loadListings(session.token);
  }, [session, editType, loadListings]);

  const handleSendOtp = async (event) => {
    event.preventDefault();
    resetMessages();
    if (!phone.trim()) {
      setError('Please enter your phone number.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/public/owners/send-otp`, { phone });
      setMessage('OTP sent.');
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    resetMessages();
    if (!otp.trim()) {
      setError('Please enter the OTP.');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/public/owners/verify-otp`, { phone, otp });
      const token = response.data?.token || '';
      if (!token) throw new Error('No token');
      setSession({ token, phone: phone.replace(/\D/g, '').slice(-10) });
      setOtp('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to verify OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    resetMessages();
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/public/owners/send-otp`, { phone });
      setMessage('OTP resent.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  const totalListings = useMemo(
    () => (listings ? Object.values(listings).reduce((sum, items) => sum + items.length, 0) : 0),
    [listings]
  );

  const editGroup = editType ? LISTING_GROUPS.find((group) => group.key === editType) : null;

  const renderLogin = () => (
    <PartnerAuth
      side={{
        line: 'Partner portal',
        points: [
          'See all your hotels, spas, tours and packages in one place',
          'Update prices, photos, policies and availability anytime',
          'Edits to live listings are reviewed and published within 72 hours',
        ],
      }}
      card={{ title: 'Welcome back', text: 'Enter your registered phone number and we will send you a one-time password.' }}
      footer={{ text: 'New to WeOtp?', to: '/list-your-property', link: 'List your property' }}
      step={step}
      phone={phone}
      otp={otp}
      loading={loading}
      error={error}
      message={message}
      onPhoneChange={setPhone}
      onOtpChange={setOtp}
      onSendOtp={handleSendOtp}
      onVerifyOtp={handleVerifyOtp}
      onResendOtp={handleResendOtp}
      onChangePhone={() => { resetMessages(); setStep('phone'); setOtp(''); }}
    />
  );

  const renderListingCard = (item) => {
    const status = STATUS_LABELS[item.status] || STATUS_LABELS.pending;
    const group = LISTING_GROUPS.find((entry) => entry.key === item.type);
    return (
      <Link
        key={`${item.type}-${item._id}`}
        to={`/owner/edit/${item.type}/${item._id}`}
        className="owner-listing-card"
        onClick={(event) => {
          // A live listing: say that edits are reviewed before going through
          if (item.status === 'active') {
            event.preventDefault();
            setPendingEdit(item);
          }
        }}
      >
        <div className="owner-listing-image">
          {item.image && (
            <img
              src={imageUrl(item.image)}
              alt=""
              loading="lazy"
              onError={(event) => { event.currentTarget.style.display = 'none'; }}
            />
          )}
          <span className="owner-listing-placeholder" aria-hidden="true">{group?.icon || '📍'}</span>
        </div>
        <div className="owner-listing-body">
          <div className="owner-listing-title">{item.name || 'Untitled'}</div>
          <div className="owner-listing-city">{item.city || '—'}</div>
          <div className="owner-listing-tags">
            <span className={`owner-status ${status.className}`}>{status.text}</span>
            {item.verified && <span className="owner-status owner-status-verified">Verified</span>}
            {item.featured && <span className="owner-status owner-status-featured">Featured</span>}
            {item.pendingChange && (
              <span className="owner-status owner-status-pending" title="Your latest edit is waiting for approval">
                Changes under review
              </span>
            )}
          </div>
        </div>
        <div className="owner-listing-edit" aria-label="Edit">
          <span className="owner-listing-edit-text">Edit</span>
          <span className="owner-listing-chevron">›</span>
        </div>
      </Link>
    );
  };

  const statusSummary = useMemo(() => {
    if (!listings) return [];
    const all = Object.values(listings).flat();
    const count = (test) => all.filter(test).length;
    return [
      { key: 'live', label: 'Live', value: count((item) => item.status === 'active'), className: 'owner-status-live' },
      { key: 'review', label: 'Under review', value: count((item) => item.status === 'pending'), className: 'owner-status-pending' },
      { key: 'paused', label: 'Paused', value: count((item) => item.status === 'inactive'), className: 'owner-status-inactive' },
    ].filter((entry) => entry.value > 0);
  }, [listings]);

  const renderDashboard = () => (
    <>
      <div className="owner-portal-head">
        <div className="partner-hero">
          <h1>My listings</h1>
          <p>
            {totalListings > 0 ? `${plural(totalListings, 'listing')} under ` : 'Logged in as '}
            <strong>{session.phone}</strong>
          </p>
          {statusSummary.length > 0 && (
            <div className="owner-summary">
              {statusSummary.map((entry) => (
                <span key={entry.key} className={`owner-status ${entry.className}`}>
                  {entry.value} {entry.label}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="owner-portal-actions">
          <Link to="/list-your-property" className="btn btn-primary">+ Add listing</Link>
        </div>
      </div>

      {message && <div className="owner-alert owner-alert-success">{message}</div>}
      {listingsError && <div className="owner-alert owner-alert-error">{listingsError}</div>}

      {listings === null ? (
        <div className="owner-card">Loading your listings…</div>
      ) : totalListings === 0 ? (
        <div className="owner-card owner-success-card">
          <h2>No listings yet</h2>
          <p>
            Nothing is registered under {session.phone} so far. If you registered with a different number,
            log out and use that one; otherwise list your first property now.
          </p>
          <Link to="/list-your-property" className="btn btn-primary">List your property</Link>
        </div>
      ) : (
        LISTING_GROUPS.map((group) => {
          const items = listings[group.key] || [];
          if (items.length === 0) return null;
          return (
            <section key={group.key} className="owner-listing-group">
              <h2>
                <span className="owner-type-icon">{group.icon}</span> {group.label}
                <span className="owner-listing-count">{items.length}</span>
              </h2>
              <div className="owner-listing-grid">{items.map(renderListingCard)}</div>
            </section>
          );
        })
      )}

      <p className="owner-portal-note">
        Tap a listing to edit it. Status, Verified and Featured are managed by the WeOtp team.
      </p>
    </>
  );

  const renderEdit = () => {
    if (!editGroup) {
      return (
        <div className="owner-card owner-success-card">
          <h2>Listing not found</h2>
          <Link to="/owner" className="btn btn-primary">Back to my listings</Link>
        </div>
      );
    }
    const Form = editGroup.form;
    return (
      <>
        <div className="partner-form-head">
          <div>
            <h2>Edit {editGroup.single}</h2>
            <p>Press Update on any step to save. Photos you remove are taken off the listing.</p>
          </div>
          <Link to="/owner" className="owner-link">Back to my listings</Link>
        </div>
        <div className="owner-form-body">
          <Form
            key={`${editType}-${editId}`}
            mode="owner"
            ownerToken={session.token}
            ownerPhone={session.phone}
            ownerEditId={editId}
            onSuccess={(result) => {
              setMessage(
                result?.pendingReview
                  ? 'Your changes were sent for review. The live listing stays as it is until our team approves them, usually within 72 hours.'
                  : result?.message === 'Nothing changed'
                    ? 'No changes to save.'
                    : 'Listing updated.'
              );
              navigate('/owner');
            }}
            onCancel={() => navigate('/owner')}
          />
        </div>
      </>
    );
  };

  const barAction = session ? (
    <button type="button" className="partner-bar-action" onClick={() => logOut('You have been logged out.')}>
      Log out
    </button>
  ) : (
    <Link to="/list-your-property" className="partner-bar-action">List your property</Link>
  );

  // Logged out: the full-page sign-in, laid out like /sy-admin/login
  if (!session) return renderLogin();

  return (
    <PartnerShell action={barAction} wide={Boolean(session)}>
      {editType ? renderEdit() : renderDashboard()}

      {pendingEdit && (
        <div className="partner-dialog-backdrop" role="presentation" onClick={() => setPendingEdit(null)}>
          <div
            className="partner-dialog partner-dialog-left"
            role="dialog"
            aria-modal="true"
            aria-labelledby="review-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="review-title">Changes Require Review</h2>
            <p>Your property is currently live. After you update the details, our team will review the changes.</p>
            {pendingEdit.pendingChange && (
              <p className="partner-dialog-note">You already have an edit waiting for review. A new edit replaces it.</p>
            )}
            <p>Your updated listing will be published within 72 hours after approval.</p>
            <p><strong>Would you like to continue editing?</strong></p>
            <div className="partner-dialog-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setPendingEdit(null)}>
                Not Now
              </button>
              <button
                type="button"
                className="btn btn-primary"
                autoFocus
                onClick={() => {
                  const target = `/owner/edit/${pendingEdit.type}/${pendingEdit._id}`;
                  setPendingEdit(null);
                  navigate(target);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </PartnerShell>
  );
};

export default OwnerPortal;
